<template>
  <div v-if="renderable_blocks.length" class="cms-blocks">
    <template v-for="block in renderable_blocks">
      <section
        v-if="block.wrap"
        :key="`cms-block-${block.type}-${block.index}`"
        class="section"
      >
        <b-container v-if="block.wrap === 'container'">
          <component :is="block.component" v-bind="block.props" />
        </b-container>
        <component :is="block.component" v-else v-bind="block.props" />
      </section>
      <component
        :is="block.component"
        v-else
        :key="`cms-block-${block.type}-${block.index}`"
        v-bind="block.props"
      />
    </template>
  </div>
</template>

<script>
  /**
   * CMS blocks dispatcher: receives the (ordered) array of a zone and renders
   * the component matching each block.
   *
   * Block format from the API: `{ component: <type>, props: {...} }`. The
   * `component` field selects the Vue component; `props` is spread onto the
   * child with `v-bind` (each key becomes a prop). Blocks with an unmapped
   * `component` are skipped (logged in dev) instead of breaking the rendering
   * of the whole page.
   *
   * The `slider` block is rendered by the shared `hero` component: CMS slides
   * arrive via the `slides` prop (spread by `v-bind`); when absent, `hero` falls
   * back to the config slides (`sliderHome.*`).
   *
   * The static homepage blocks (point list, value proposition, info banner,
   * image with CTA) have no section of their own: the dispatcher wraps them as
   * the static pages did. Sites add their own blocks through `site_blocks`.
   */
  import { get_renderable_blocks } from './content-utilities';
  import Hero from './hero.vue';
  import TextImage from './text-image.vue';
  import TextImageAlt from './text-image-alt.vue';
  import TextColumns from './text-columns.vue';
  import Assistance from './assistance.vue';
  import InfoBanner from './info-banner.vue';
  import PointList01 from '@/components/library/blocks/point-list-01.vue';
  import ValueProposition from '@/components/library/blocks/value-proposition.vue';
  import ImageWithTextCta from '@/components/library/blocks/image-with-text-cta.vue';

  // API `component` field value => Vue component tag
  const BLOCK_COMPONENTS = {
    slider: 'hero',
    'text-image': 'text-image',
    'text-image-alt': 'text-image-alt',
    'text-columns': 'text-columns',
    assistance: 'assistance',
    'info-banner': 'info-banner',
    'point-list': 'point-list-01',
    'value-proposition': 'value-proposition',
    'image-with-text-cta': 'image-with-text-cta',
  };

  // Block type => wrapper: 'container' (section + container) or 'section'
  const BLOCK_WRAPS = {
    'info-banner': 'container',
    'point-list': 'container',
    'value-proposition': 'container',
    'image-with-text-cta': 'section',
  };

  export default {
    name: 'content-blocks',

    components: {
      hero: Hero,
      'text-image': TextImage,
      'text-image-alt': TextImageAlt,
      'text-columns': TextColumns,
      assistance: Assistance,
      'info-banner': InfoBanner,
      'point-list-01': PointList01,
      'value-proposition': ValueProposition,
      'image-with-text-cta': ImageWithTextCta,
    },

    props: {
      blocks: {
        type: Array,
        default: () => [],
      },
      // Site-specific blocks: type => { component, wrap }
      site_blocks: {
        type: Object,
        default: () => ({}),
      },
    },

    computed: {
      renderable_blocks() {
        const site_components = Object.keys(this.site_blocks).reduce(
          (acc, type) => ({ ...acc, [type]: this.site_blocks[type].component }),
          {}
        );
        return get_renderable_blocks(
          this.blocks,
          { ...BLOCK_COMPONENTS, ...site_components },
          (type) =>
            this.log(`[content-blocks] unhandled block component: ${type}`)
        ).map((block) => ({
          ...block,
          wrap: (this.site_blocks[block.type] || {}).wrap || BLOCK_WRAPS[block.type],
        }));
      },
    },
  };
</script>
